import { add, norm, sub } from "../../math.ts";
import { Btn } from "../../net/input.ts";
import { abilIdx, ActionKind } from "../../sim/components.ts";
import { applyDamage, applyStatus, damageBarrier, explode, isEnemy, playersInRadius } from "../../sim/combat.ts";
import { damageConstruct, nearestEnemy, spawnConstruct } from "../../sim/constructs.ts";
import { targetPoint } from "../../sim/targeting.ts";
import { HeroId, Role, Slot, type HeroDef } from "../types.ts";
import { ck, fxk, pk, type HeroModule } from "../module.ts";
import { action, commonInput, constructHitscan, consume, glide, groundAimPoint, launchProjectile, ownerAlive, ready, secs, simpleProjectile, useUlt } from "../kitlib.ts";

export const PK = { Trident: pk(HeroId.SubMarinara, 0), Wrath: pk(HeroId.SubMarinara, 1) } as const;
export const CK = { Monstro: ck(HeroId.SubMarinara, 0) } as const;
export const FX = { Trident: fxk(HeroId.SubMarinara, 0), Monstro: fxk(HeroId.SubMarinara, 1), Deep: fxk(HeroId.SubMarinara, 2) } as const;

const def: HeroDef = {
  id: HeroId.SubMarinara,
  slug: "sub-marinara",
  name: "Sub-Marinara",
  role: Role.Damage,
  color: "#2bb3a8",
  blurb: "A briny trident king who commands pasta-sea spawn turrets and drops a monster from below.",
  health: 275,
  armor: 0,
  shields: 0,
  radius: 0.42,
  height: 1.85,
  headRadius: 0.23,
  speed: 6,
  jumpSpeed: 7.45,
  ammo: 0,
  reloadTime: 0,
  ultCost: 3100,
  abilities: {
    [Slot.Primary]: { name: "Trident al Dente", key: "LMB", description: "Throw a fast trident that damages enemies, hastens your next spawn, and enrages spawn on crit.", cooldown: 0 },
    [Slot.Secondary]: { name: "Seven-Sea Sauce", key: "RMB", description: "Throw a command trident that bursts, enrages spawn, and orders them to focus the hit target.", cooldown: 6 },
    [Slot.Ability1]: { name: "Deep-Dish Blessing", key: "Shift", description: "Leap upward inside invulnerable water for 3 seconds.", cooldown: 20 },
    [Slot.Ability2]: { name: "Aquatic Dominiono", key: "E", description: "Summon a sea-spawn turret that shoots nearby enemies.", cooldown: 10, charges: 2 },
    [Slot.Ultimate]: { name: "Horn of Provolone", key: "Q", description: "Aim, then LMB to call a giant sea beast that crushes enemies in a wide area.", cooldown: 0 },
  },
  passive: { name: "Tide Fall", description: "Hold Space in the air to glide downward." },
};

const module: HeroModule = {
  def,
  kit: {
    init() {},
    tick(sim, eid, input, pressed, canAct) {
      const c = sim.c;
      commonInput(sim, eid, input, pressed);
      if (!canAct) return;
      if (useUlt(sim, eid, input, pressed)) {
        const pos = targetPoint(sim, eid, Slot.Ultimate).pos;
        sim.emit({ type: "explosion", pos, radius: 9, kind: FX.Deep });
        for (const { eid: t, d } of playersInRadius(sim, add(pos, { x: 0, y: 1.8, z: 0 }), 9, (e) => isEnemy(sim, eid, e))) {
          applyDamage(sim, t, d <= 3.5 ? 500 : 180, eid, { pos });
          applyStatus(sim, t, "knockdown", 1.5, { source: eid });
        }
      }
      if (pressed & Btn.Ability1 && ready(sim, eid, Slot.Ability1)) {
        consume(sim, eid, Slot.Ability1);
        action(sim, eid, ActionKind.Cast, 18);
        applyStatus(sim, eid, "invuln", 3, { source: eid });
        applyStatus(sim, eid, "unstoppable", 3, { source: eid });
        c.Vel.y[eid] = 11.4;
        c.Player.grounded[eid] = 0;
      }
      if (pressed & Btn.Ability2 && ready(sim, eid, Slot.Ability2)) {
        consume(sim, eid, Slot.Ability2);
        action(sim, eid, ActionKind.Cast, 14);
        spawnConstruct(sim, eid, CK.Monstro, groundAimPoint(sim, eid, 30, 0.65), input.yaw, { seconds: 8 });
      }
      if (pressed & Btn.Secondary && ready(sim, eid, Slot.Secondary)) {
        consume(sim, eid, Slot.Secondary);
        action(sim, eid, ActionKind.AltFire, 12);
        launchProjectile(sim, eid, PK.Wrath, 150, input, { radius: 0.3, seconds: 0.45 });
      }
      if (input.buttons & Btn.Primary) {
        if (c.Weapon.fireCd[eid]! <= 0) {
          c.Weapon.shots[eid] = (c.Weapon.shots[eid]! + 1) & 0xffff;
          c.Weapon.fireCd[eid] = 55;
          action(sim, eid, ActionKind.Fire, 12);
          launchProjectile(sim, eid, PK.Trident, 150, input, { radius: 0.3, seconds: 0.55 });
        }
      }
    },
    movement(sim, eid, input, mods) {
      if (input.buttons & Btn.Jump) glide(sim, eid, input, mods, { fallSpeed: 3.5, driftSpeed: 6 });
      if (sim.c.Status.invuln[eid]! > 0) mods.speedMul *= 0.7;
    },
  },
  targets: {
    [Slot.Ultimate]: { range: 30, radius: 9, verb: "Call" },
  },
  constructs: {
    [CK.Monstro]: {
      maxHp: 125,
      seconds: 8,
      shape: { kind: "cyl", r: 0.55, h: 1.2 },
      solid: true,
      tick(sim, e) {
        const c = sim.c;
        const berserk = c.Construct.v0[e]! > 0;
        if (berserk) c.Construct.v0[e] = Math.max(0, c.Construct.v0[e]! - 1);
        if (c.Construct.ticks[e]! % (berserk ? 12 : 30) !== 0) return;
        const owner = c.Construct.owner[e]!;
        const target = nearestEnemy(sim, owner, add(sim.posOf(e), { x: 0, y: 0.6, z: 0 }), 40, { los: true });
        if (!target) return;
        const from = add(sim.posOf(e), { x: 0, y: 0.65, z: 0 });
        const to = target.construct ? sim.posOf(target.eid) : add(sim.posOf(target.eid), { x: 0, y: sim.hero(target.eid).height * 0.55, z: 0 });
        const dir = norm(sub(to, from));
        constructHitscan(sim, e, dir, { range: 40, damage: berserk ? 12 : 18, falloffStart: 20, falloffEnd: 40, falloffMin: berserk ? 0.3 : 0.5, canCrit: true, fxKind: FX.Monstro, height: 0.65 });
      },
      visual: { color: "#2bb3a8", shape: "cyl" },
    },
  },
  projectiles: {
    [PK.Trident]: simpleProjectile(
      (sim, _p, owner, hit) => {
        if (!ownerAlive(sim, owner)) return;
        if (hit.target !== undefined) {
          const i = abilIdx(owner, Slot.Ability2);
          sim.c.Abil.cd[i] = Math.max(0, sim.c.Abil.cd[i]! - secs(1.3));
          applyDamage(sim, hit.target, 80, owner, { crit: hit.head, pos: hit.pos });
          if (hit.head) for (const m of sim.constructEids()) if (sim.c.Construct.owner[m] === owner && sim.c.Construct.kind[m] === CK.Monstro) sim.c.Construct.v0[m] = secs(2);
        } else if (hit.construct !== undefined) damageConstruct(sim, hit.construct, 80, owner);
        else if (hit.barrier !== undefined) damageBarrier(sim, hit.barrier, 80, owner);
        else sim.emit({ type: "impact", pos: hit.pos, kind: FX.Trident });
      },
      { color: "#72fff1", size: 0.22, trail: true },
    ),
    [PK.Wrath]: simpleProjectile(
      (sim, _p, owner, hit) => {
        if (!ownerAlive(sim, owner)) return;
        let focus: number | undefined;
        if (hit.target !== undefined) {
          focus = hit.target;
          applyDamage(sim, hit.target, 40, owner, { pos: hit.pos });
          for (const m of sim.constructEids()) if (sim.c.Construct.owner[m] === owner && sim.c.Construct.kind[m] === CK.Monstro) sim.c.Construct.v0[m] = secs(2);
        }
        explode(sim, owner, hit.pos, { radius: 3, damage: 40, minDamage: 20, knockback: 3, fxKind: FX.Deep });
        if (focus !== undefined) {
          for (const m of sim.constructEids()) {
            if (sim.c.Construct.owner[m] !== owner || sim.c.Construct.kind[m] !== CK.Monstro) continue;
            const from = add(sim.posOf(m), { x: 0, y: 0.65, z: 0 });
            const dir = norm(sub(add(sim.posOf(focus), { x: 0, y: sim.hero(focus).height * 0.55, z: 0 }), from));
            constructHitscan(sim, m, dir, { range: 45, damage: 60, canCrit: true, fxKind: FX.Monstro, height: 0.65 });
          }
        }
      },
      { color: "#b7fff7", size: 0.28, trail: true },
    ),
  },
  fx: {
    [FX.Trident]: { color: "#72fff1" },
    [FX.Monstro]: { color: "#29d0b8" },
    [FX.Deep]: { color: "#0a7f8f" },
  },
};

export default module;
